import {isUndefined} from 'lodash';
import React, {Fragment} from 'react';

import {renderedLanguagesMap} from '../../../../libs/knowledge-base/shared/helpers/knowledgeBaseMultiLangHelpers';
import {KnowledgeBaseSupportedLanguagesTypesEnum} from '../../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {MultiSelectFilterDropdown} from '../../../components/common/filters/multiSelectFilter/multiSelectFilterDropdown';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.LOCALE];

/*
 * Hook.
 */

export const useAnalyticsLocaleFilter = (
  localeOptions: ReadonlyArray<KnowledgeBaseSupportedLanguagesTypesEnum> = [],
) => {
  const [selectedLocales, onUpdate] = useAnalyticsFilter(AnalyticsFilterTypesEnum.LOCALE);
  const {trackOnEdit} = useFiltersTracking(AnalyticsFilterTypesEnum.LOCALE);
  const isActive = !isUndefined(selectedLocales) && selectedLocales?.length > 0;

  const localeMap = localeOptions.reduce((acc, locale) => ({...acc, [locale]: locale}), {});
  const renderButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={selectedLocales}
      itemsMap={localeMap}
      renderItem={renderButtonItem}
      iconName={isActive ? activeIconName : iconName}
      label={name}
      isActive={isActive}
      shouldLabelSelection={false}
    />
  );

  const renderDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <MultiSelectFilterDropdown
      {...dropdownProps}
      title={undefined}
      width={140}
      showClearButton={false}
      onClear={() => onUpdate([])}
      rows={localeOptions.map((locale) => ({
        renderer: () => <DropdownItemContent>{renderedLanguagesMap[locale]}</DropdownItemContent>,
        isSelected: selectedLocales?.includes(locale) ?? false,
        onSelect: () => {
          trackOnEdit([locale], selectedLocales);
          onUpdate([locale]);
        },
      }))}
      isMultiSelect={false}
    />
  );

  return {
    id: 'locale-filter',
    type: AnalyticsFilterTypesEnum.LOCALE,
    isVisible: localeOptions.length > 1,
    isActive,
    title: renderFilterTitle(name, iconName),
    renderDropdown,
    renderButton,
  };
};

/*
 * Helpers.
 */

function renderButtonItem(locale: KnowledgeBaseSupportedLanguagesTypesEnum) {
  return <Fragment key={locale}>{renderedLanguagesMap[locale]}</Fragment>;
}
